import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BillingDetailsService } from './billing-details.service';
import { PolicyDataService } from '../data-services/policy-data.service';
import { environment } from '@env/environment';
import * as _ from 'underscore';
import { UserDataService } from '../data-services/user-data.service';
import { BankAccountService } from '../profile-settings/bank-account.service';
import { StorageServiceObservablesService } from '../storage-service-observables/storage-service-observables.service';
import { CustomerProfileService } from '../customer-profile/customer-profile.service';
import { Router } from '@angular/router';
import { CustomerBannerService } from '@services/banner/customer-banner/customer-banner.service';
import { AcmicService } from '@mapfre-design-library/src/public_api';
import { PifDiscountInfo } from '@models/billing';
import {
  isExpiringAS400Policy,
  isExpiringLivoPolicy,
} from '@helpers/policy-eligibility.utils';

@Injectable({
  providedIn: 'root'
})
export class PolicyDetailsService {
  policyResponse: any;
  customerProfileData: any;
  upto14DaysToRenual: any = [];
  userData: any;
  isAcmic: boolean;
  constructor(
    private http: HttpClient,
    private policyDataService: PolicyDataService,
    private billingDetailsService: BillingDetailsService,
    private userDataService: UserDataService,
    private bankAccountService: BankAccountService,
    private storageServiceObservablesService: StorageServiceObservablesService,
    private cps: CustomerProfileService,
    private customerBannerService: CustomerBannerService,
    public router: Router,
    private acmicService: AcmicService,
  ) { 
    this.isAcmic = this.acmicService.isACMIC();
  }

  getDayDifference(expDate) {
    const dt1 = new Date();
    let finalDay = 0;
    if (expDate) {
      const newDate = expDate.split('T');
      if (newDate.length > 0) {
        const dt2 = new Date(newDate[0]);
        finalDay = Math.floor(
          (Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()) -
            Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate())) /
          (1000 * 60 * 60 * 24)
        );
      }
    }
    return finalDay;
  }

  isPolicyActiveForMoreThan5Months(effDate): boolean {
    const effMonth = new Date(effDate).getMonth();
    return new Date(new Date(effDate).setMonth(effMonth + 5)) < new Date();
  }

  getPolicyDetailsByEmail(email: string) {
    const policyBillingDataAll: any[] = [];
    const url = `${environment.backend_server_url_account}/${email}`;
    return this.http.get(url).pipe(
      map((policyResponse: any[]) => {
        if(!this.isAcmic) {
          this.customerBannerService.loadAllCustomerBanners(policyResponse);
        }
        const observablesList = [];
        this.policyDataService.setPolicyList(policyResponse);
        // Phase 1: emit immediately with raw policy metadata so all subscribers
        // (sidenav, widgets, header, profile, claims, docs, etc.) can render
        // without waiting for billing-summary calls.
        // this.policyDataService.updatePolicyDetails(policyResponse);
        policyResponse.forEach((policy) => {
          observablesList.push(
            this.billingDetailsService
              .getBillingSummaryByPolicy(policy.policynumber.policyNumber)
              .pipe(catchError(() => of(false)))
          );
        });

        forkJoin(observablesList).subscribe((results) => {
          results.forEach((result: any, i) => {
      
            policyResponse[i]['days_left_to_expire'] = this.getDayDifference(
              policyResponse[i]['expDate']
            );
            policyResponse[i]['isNewPolicy'] = false;
            this.completeConvertedPolicy(email, policyResponse[i]);
            if (result && result.currentBills) {

              if (result.eft || result.isEFT) {
                policyResponse[i].policyFlags.isEft = 'ENROLLED'
              }

              policyBillingDataAll.push(
                ...[
                  _.extend(
                    policyResponse[i],
                    {
                      billingDetails: { ...result.currentBills, mortgagee: result.mortgagee},
                      historyBills: [...this.mapTransactionTime(result.historyBills, result.transactions)],
                      scheduleBills: [...result.scheduleBills],
                      transactions: [...result.transactions],
                      hasAffinityGroup: result.hasAffinityGroup,
                      policyCancellationEffectiveDate:
                        result.policyCancellationEffectiveDate,
                      policyCancellationReason: result.policyCancellationReason,
                      originalCancelAmount: result.originalCancelAmount,
                      exitDelinquencyAmount: result.exitDelinquencyAmount,
                      paymentPlan: result.paymentPlan,
                      priorTotalPolicyPremium: result.priorTotalPolicyPremium,
                      priorYearBalance: result.priorYearBalance,
                      invoiceNumber: result.invoiceNumber,
                      totalPolicyPremium: result.totalPolicyPremium,
                      priorYearBalanceDueDate: result.priorYearBalanceDueDate,
                      hasPriorYearBalance: result.hasPriorYearBalance,
                      daysAfterCancellation: this.getDayDifference(
                        result.policyCancellationEffectiveDate
                      ),
                      checkDigits: result.checkDigits,
                      as400AccountId: result.accountId,
                      isDelinquency: this.isDelinquency(policyResponse[i], result.policyCancellationReason),
                      isSimplifiedPayPlan: result.isSimplifiedPayPlan ?? false,
                      pifDiscountInfo: this.computePifEligible(result, policyResponse[i]),
                      termNumber: result.termNumber ?? 1,
                    }
                  ),
                ]
              );
            } else {
              policyBillingDataAll.push(
                ...[
                  _.extend(
                    policyResponse[i],
                    { billingDetails: false }
                  ),
                ]
              );
            }
          });
          const expiringPolicyArray = _.filter(policyBillingDataAll, isExpiringAS400Policy);
          const expiringLivoPolicyArray = _.filter(policyBillingDataAll, isExpiringLivoPolicy);
          const activePoliciesListAfter5Months = _.filter(
            policyBillingDataAll,
            (policy) => {
              return (
                policy.policyTerm === 12 &&
                policy.policynumber.policyNumber.length === 10 &&
                policy.policyStatus === 'ACTIVE' &&
                (policy.policyType === 'AUTO' ||
                  policy.policyType === 'HOME' ||
                  policy.policyType === 'UMBRELLA' ||
                  policy.policyType === 'DWELLING_FIRE_PERSONAL') &&
                this.isPolicyActiveForMoreThan5Months(policy.effDate)
              );
            }
          );
          const eligibleForPopupPolicies = _.filter(
            policyBillingDataAll,
            (policy) => {
              return (
                policy.policyStatus === 'ACTIVE' && policy.eligibleForPopup
              );
            }
          );
          this.getExpiringPolicies(expiringPolicyArray);
          this.getRenewalPolicyList(expiringPolicyArray);
          this.policyDataService.updateExpiringLivoPolicies(
            expiringLivoPolicyArray
          );
          if (expiringLivoPolicyArray && expiringLivoPolicyArray.length) {
            this.upto14DaysToRenual = _.filter(
              expiringLivoPolicyArray,
              (policy) => {
                return policy.days_left_to_expire <= 14;
              }
            );
          }
          const cpData = JSON.parse(localStorage.getItem('cpModalsnoozeSince'));
          const isFirstimeLogin = JSON.parse(
            localStorage.getItem('isFirstimeLogin')
          );
          if (policyResponse[0].displayPhone) {
            if (
              eligibleForPopupPolicies &&
              eligibleForPopupPolicies.length &&
              this.upto14DaysToRenual &&
              this.upto14DaysToRenual.length
            ) {
              this.cps.setTimeFrameModal('PAPERLESS_POPUP');
            } else if (
              expiringLivoPolicyArray &&
              expiringLivoPolicyArray.length
            ) {
              this.cps.setTimeFrameModal('EXPIRES_30_DAYS');
            } else if (
              activePoliciesListAfter5Months &&
              activePoliciesListAfter5Months.length
            ) {
              this.cps.setTimeFrameModal('AFTER_5_MONTHS');
            } else if (cpData && cpData.daysSinceLastLogin > 89) {
              this.cps.setTimeFrameModal('NOT_LOGGEDIN_90_DAYS');
            } else if (
              isFirstimeLogin === true ||
              (eligibleForPopupPolicies && eligibleForPopupPolicies.length)
            ) {
              this.cps.setTimeFrameModal('PAPERLESS_POPUP');
            }
          } else if (
            isFirstimeLogin === true ||
            (eligibleForPopupPolicies && eligibleForPopupPolicies.length)
          ) {
            this.cps.setTimeFrameModal('PAPERLESS_POPUP');
          }
          this.policyDataService.updatePolicyDetails(policyBillingDataAll);
          if (localStorage.getItem('isFirstimeLogin')) {
            localStorage.removeItem('isFirstimeLogin');
          }
        });
      })
    );
  }

  isDelinquency(policy: any, cancellationReason: string): boolean {
    if (policy?.company?.companyName === 'ACIC' && policy?.policyStatus === 'CANCELLED')
      return true;

    return cancellationReason
      && (cancellationReason.toLowerCase() == 'pay - non-payment of insurance premium'
        || cancellationReason.toLowerCase() == 'fpd - failure to pay deposit')
  }

  computePifEligible(billingInfo: any, policyInfo: any): PifDiscountInfo | null {
    if (!billingInfo?.pifDiscountInfo) return null;

    const outstandingBalance = (billingInfo.currentBills?.[0]?.outstandingbalance ?? 0) 
                                + (billingInfo.currentBills?.[0]?.priorYearBalance ?? 0);
    const pifDiscountInfo: PifDiscountInfo = { 
      ...billingInfo.pifDiscountInfo,
      pifEligible: false,
      isPaidInFull: false
    };
    const termNumber = billingInfo?.termNumber ?? 1;
    const isRenewalPolicy = isExpiringLivoPolicy(policyInfo, ['AUTO']);
    const isEligibleForPifCheck = termNumber > 1 || isRenewalPolicy;

    if (!isEligibleForPifCheck) return pifDiscountInfo;

    const isPifWindowActive = this.isPifDiscountWindowActive(policyInfo, pifDiscountInfo.pifDiscountAvailableUntil);
    pifDiscountInfo.pifEligible = !!pifDiscountInfo.isPifDiscountEligible && isPifWindowActive;
    pifDiscountInfo.isPaidInFull = isPifWindowActive
      ? (!!pifDiscountInfo.pifDiscountApplied && !!pifDiscountInfo.isPifDiscountEligible && outstandingBalance <= 0)
      : !!pifDiscountInfo.pifDiscountApplied;

    return pifDiscountInfo;
  }

  mapTransactionTime(historyBills: any[], transactions: any[]): any[] {
    const historyTransactions: any[] = historyBills.map(bill => {
      const matchTransaction = transactions.find(trans =>
        trans.transactionDate === bill.datePaid
        && ((bill.referenceNumber && trans.description.includes("Reference #") && trans.description.includes(bill.referenceNumber))
          || (trans.netAmount < 0 && (Math.abs(trans.netAmount) === Math.abs(bill.amountPaid))
            && trans.description.includes(bill.amountPaid)))
      )

      return matchTransaction
        ? { ...bill, transactionTime: matchTransaction.transactionTime }
        : bill;
    })

    return historyTransactions;
  }

  getExpiringPolicies(policies) {
    const policyObs = [];
    const observablesList = [];
    const obserList = [];
    const policyBillingDataAll: any[] = [];
    const livoPolicies = [];
    policies.forEach((policy) => {
      obserList.push(
        this.getRenewalPolicyByLegacyPolicyNumber(
          policy.policynumber.policyNumber
        ).pipe(catchError(() => of(false)))
      );
    });

    forkJoin(obserList).subscribe((results) => {
      results.forEach((list: any) => {
        livoPolicies.push(list.policyNumber);
        policyObs.push(
          this.getPolicyDetailsByNumber(list.policyNumber).pipe(
            catchError(() => of(false))
          )
        );
      });
    });

    forkJoin(policyObs).subscribe((policydata) => {
      policydata.forEach((results: any) => {
        livoPolicies.forEach(() => {
          observablesList.push(
            this.billingDetailsService
              .getBillingSummaryByPolicy(
                results.policynumber.policypolicyNumber
              )
              .pipe(catchError(() => of(false)))
          );
        });

        forkJoin(observablesList).subscribe((data) => {
          data.forEach((result: any, indx) => {
            policies[indx]['days_left_to_expire'] = this.getDayDifference(
              policies[indx]['expDate']
            );
            policies[indx]['isNewPolicy'] = false;
            if (result && result.currentBills) {
              policyBillingDataAll.push(
                ...[
                  _.extend(
                    policies[indx],
                    {
                      billingDetails: { ...result.currentBills },
                      historyBills: [...this.mapTransactionTime(result.historyBills, result.transactions)],
                      scheduleBills: [...result.scheduleBills],
                      hasAffinityGroup: result.hasAffinityGroup,
                      policyCancellationEffectiveDate:
                        result.policyCancellationEffectiveDate,
                      policyCancellationReason: result.policyCancellationReason,
                      originalCancelAmount: result.originalCancelAmount,
                      exitDelinquencyAmount: result.exitDelinquencyAmount,
                      daysAfterCancellation: this.getDayDifference(
                        result.policyCancellationEffectiveDate
                      ),
                      pifDiscountInfo: this.computePifEligible(result, policies[indx])
                    }
                  ),
                ]
              );
            } else {
              policyBillingDataAll.push(
                ...[
                  _.extend(
                    policies[indx],
                    { billingDetails: false }
                  ),
                ]
              );
            }
          });
        });
      });
    });

    this.policyDataService.$policyDetails.pipe(
      map((policyData) => {
        const billingpolicies = policyData.concat(policyBillingDataAll);
        this.policyDataService.updatePolicyDetails(billingpolicies);
      })
    );
  }

  getPolicyData(policyNumber) {
    const url = `${environment.backend_server_url_policy}/${policyNumber}/trimmed`;
    return this.http.get(url);
  }
  getPoliciesByEmail(email) {
    const url = `${environment.backend_server_url_account}/${email}`;
    return this.http.get(url);
  }

  getDocumentsByPolicy(policyNumber: string): Observable<any> {
    const url = `${environment.backend_server_url_policy}/documents?policyNumber=${policyNumber}&docTypeFlagRq=pc`;
    return this.http.get(url);
  }

  getInvoiceByPolicy(
    policyNumber: string,
    invoiceNumber: string
  ): Observable<any> {
    const url = `${environment.backend_server_url_policy}/invoice?policyNumber=${policyNumber}&docTypeFlagRq=bc&invoiceNumber=${invoiceNumber}`;
    return this.http.get(url);
  }

  getDocumentById(documentId: string, refernceId: string): Observable<any> {
    const url = `${environment.backend_server_url_policy}/document/getPresignedDoc/${refernceId}/${refernceId}/${documentId}`;
    return this.http.get(url);
  }

  getDocumentByIdForClaim(policyNumber: string, refernceId: string, documentId: string): Observable<any> {
    const url = `${environment.backend_server_url_policy}/document/getPresignedDoc/${policyNumber}/${refernceId}/${documentId}`;
    return this.http.get(url);
  }

  getDocumentByIdOld(documentId: string, refernceId: string): Observable<any> {
    void refernceId;
    const url = `${environment.backend_server_url_policy}/document/${documentId}`;
    return this.http.get(url);
  }

  getDocumentByIdNew(documentId: string): Observable<any> {
    const url = `${environment.backend_server_url_policy}/document/getFile/${documentId}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  getPresignedDocumentById(documentId: string): Observable<any> {
    const url = `${environment.backend_server_url_policy}/document/signed/${documentId}`;
    return this.http.get(url);
  }

  getPreSignedDocumentsForUpload(sourceSystem: string, refernceId: string, docSource: string, fileName: string, isLegacy = false, type: string, ecmDocCode: string, policyNumber: string): Observable<any> {
    // https://z0k7s69fhi.execute-api.us-east-1.amazonaws.com/v1/bff/b2c-account-api/GW/7000091981/POLICY/Test.pdf/documents?isLegacy=false&type=Inspection%20Validation&ecmDocCode=ENDREQ
    const url = `${environment.backend_server_url_account}/${sourceSystem}/${refernceId}/${docSource}/${fileName}/${policyNumber}/documents?isLegacy=${isLegacy}&type=${type}&ecmDocCode=${ecmDocCode}`;
    return this.http.get(url);
  }

  sendPresignedUploadDocument(url: string, payload: any) {
    return this.http.put(url, payload, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  getUploadFileStatus(transactionId, policyNumber): Observable<any> {
    //https://z0k7s69fhi.execute-api.us-east-1.amazonaws.com/v1/bff/b2c-account-api/{transID}/documents/status
    const url = `${environment.backend_server_url_account}/${policyNumber}/${transactionId}/documents/status`;
    return this.http.get(url, { responseType: 'text' });
  }

  getDocumentByIdFileName(
    documentId: string,
    fileName: string
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/pdf');
    const url = `${environment.backend_server_url_policy}/view-document/${documentId}/${fileName}`;
    return this.http.get(url, { headers, responseType: 'blob' });
  }

  getVehicleByPolicy(policyNumber) {
    const url = `${environment.backend_server_url_policy}/${policyNumber}/vehicles`;
    return this.http.get(url);
  }

  updateMileageById(email, policyNumber, vehicleId, odometerReading) {
    const url = `${environment.backend_server_url_policy}/${email}/${policyNumber}/${vehicleId}?odometerReading=${odometerReading}`;
    return this.http.post(url, {});
  }

  getPolicyDetailsByNumber(policyNumber) {
    const url = `${environment.backend_server_url_policy}/?policynumber=${policyNumber}`;
    return this.http.get(url);
  }

  addPolicyToEmail(email, policyNumber) {
    const url = `${environment.backend_server_url_account}/${email}/${policyNumber}/add-policy`;
    return this.http.put(url, {});
  }

  getRenewalPolicyByLegacyPolicyNumber(policyNumber: string): Observable<any> {
    const url = `${environment.backend_server_url_policy}/${policyNumber}/preRenewal-policy`;
    return this.http.get(url);
  }

  checkIfConvertedPolicy(policyNumber: string): Observable<any> {
    const url = `${environment.backend_server_url_policy}/${policyNumber}/check-conversion`;
    return this.http.get(url);
  }

  checkIfExists(policyNumber: string): Observable<any> {
    const url = `${environment.backend_server_url_policy}/${policyNumber}/exists`;
    return this.http.get(url);
  }

  checkPolicyExists(policyNumber: string) {
    const url = `${environment.backend_server_url_account}/policy/${policyNumber}`;
    return this.http.get(url);
  }

  getRenewalPolicyList(expiringPolicy) {
    const obserList = [];
    const renewalPolicyList: any[] = [];
    if (expiringPolicy) {
      expiringPolicy.forEach((policy) => {
        obserList.push(
          this.getRenewalPolicyByLegacyPolicyNumber(
            policy.policynumber.policyNumber
          ).pipe(catchError(() => of(false)))
        );
      });
      forkJoin(obserList).subscribe((results) => {
        if (results) {
          results.forEach((list: any, i) => {
            expiringPolicy[i]['livoPolicy'] = list.policyNumber;
            expiringPolicy[i]['legacyPolicyNumber'] = list.legacyPolicyNumber;
            expiringPolicy[i]['isConverted'] = true;
            expiringPolicy[i]['underConversion'] = true;
            renewalPolicyList.push(expiringPolicy[i]);
          });
          this.getLivoPolicyDetails(renewalPolicyList);
          this.policyDataService.updateRenewalPolicy(renewalPolicyList);
        }
      });
    }
  }

  getLivoPolicyDetails(policys) {
    let policyData = [];
    this.policyDataService.$policyDetails.subscribe((policyResponse: any) => {
      if (policyResponse) {
        policyData = policyResponse;
      }
    });

    if (policyData) {
      policys.forEach((list) => {
        if (list.livoPolicy) {
          const billingDetails = this.getNewGWBill(list.livoPolicy);
          this.getPolicyDetailsByNumber(list.livoPolicy).subscribe(
            (details) => {
              details['days_left_to_expire'] = list.days_left_to_expire;
              details['isNewPolicy'] = true;
              details['legacyPolicyNumber'] = list.legacyPolicyNumber;
              details['allowedPayment'] = list.days_left_to_expire < 21;

              let alreadyExists = false;
              alreadyExists =
                _.filter(
                  this.policyDataService.getPolicyList(),
                  () => {
                    return true;
                  }
                ).length > 0;

              if (!alreadyExists) {
                policyData.push(
                  ...[
                    _.extend(details, {
                      billingDetails: { ...billingDetails },
                    }),
                  ]
                );
                this.policyDataService.setPolicyList(policyData);
                this.policyDataService.updatePolicyDetails(policyData);
              }
            }
          );
        }
      });
    }
  }

  getBankData(policyList) {
    const bankobserList = [];
    const email = this.storageServiceObservablesService.getUserFromStorage();
    this.userDataService.$userData.subscribe((user) => {
      this.userData = user;
    });
    if (this.userData != undefined) {
      if (policyList) {
        policyList.forEach((element) => {
          bankobserList.push(
            this.bankAccountService
              .getBankAccountByEmailWithPolicyNumber(email, element)
              .pipe(catchError(() => of(false)))
          );
        });
        forkJoin(bankobserList).subscribe((results) => {
          if (results) {
            results.forEach((list: any) => {
              this.userData.bankAccountDetails.push(list);
            });
            this.userDataService.updateUserDetails(this.userData);
          }
        });
      }
    } else {
      if (policyList) {
        const newUserData: any = [];
        policyList.forEach((element) => {
          bankobserList.push(
            this.bankAccountService
              .getBankAccountByEmailWithPolicyNumber(email, element)
              .pipe(catchError(() => of(false)))
          );
        });
        const bankAccountDetails: any = [];
        forkJoin(bankobserList).subscribe((results) => {
          if (results) {
            results.forEach((list: any) => {
              bankAccountDetails.push(list);
            });
            newUserData.push(bankAccountDetails);
            this.userDataService.updateUserDetails(newUserData);
          }
        });
      }
    }
  }

  getNewGWBill(policyId) {
    const billingDetails: any = [];
    this.billingDetailsService.getCurrentBillByPolicy(policyId).subscribe(
      (bill) => {
        if (bill) {
          billingDetails.push(bill[0]);
        }
      }
    );

    return billingDetails;
  }
  getPolicyFlags(policyNumber) {
    const url = `${environment.backend_server_url_policy}/${policyNumber}/policyFlags`;
    return this.http.get(url);
  }

  completeConvertedPolicy(email, policy) {
    const policyNumber = policy['policynumber']['policyNumber'];

    if (
      (policyNumber.length === 6 &&
        policy['policyStatus'] === 'ACTIVE' &&
        policy['days_left_to_expire'] <= 30) ||
      (policyNumber.length === 6 &&
        policy['policyStatus'] === 'INACTIVE' &&
        policy['days_left_to_expire'] <= 30)
    ) {
      // get information to determine if policy has been converted and what the new policy number is.
      this.getRenewalPolicyByLegacyPolicyNumber(policyNumber).subscribe(
        (details) => {
          if (details['isConvertedPolicy'] === true) {
            // add converted policy number to the customer (via the email in process).
            this.addPolicyToEmail(email, details['policyNumber']).subscribe(
              () => {
                // add the public id to the customer (if applicable per rules on the server side).
                this.addPublicId(
                  email,
                  details['publicId'],
                  details['policyNumber']
                ).subscribe(
                  () => {
                    //do nothing
                  }
                );
              }
            );
          }
        }
      );
    }
  }

  addPublicId(email, publicId, policyNumber) {
    const url = `${environment.backend_server_url_account}/publicid/add-publicid?email=${email}&publicId=${publicId}&policyNumber=${policyNumber}`;
    return this.http.put(url, {});
  }

  checkPolicyForGuestPayment(policyNumber) {
    const url = `${environment.backend_server_url_policy}/${policyNumber}/checkPolicyForGuestPayment`;
    return this.http.get(url, {});
  }

  isEftEligiblePolicies(policyArray: any[], isAcmic: boolean): boolean {
    return policyArray.some((policy) => {
      if (policy) {
        let dayDifference: any;
        const { effDate } = policy;
        if (effDate) {
          dayDifference = this.getDayDifference(effDate);
        }
        return (
          ((policy.policyFlags.isEftEligible === 'Y' &&
            policy.isNewPolicy &&
            dayDifference <= 0) ||
            (policy.policyFlags.isEftEligible === 'Y' &&
              !policy.isNewPolicy)) &&
          policy.policyStatus === 'ACTIVE' &&
          policy.billingDetails[0] &&
          (policy.billingDetails[0].badCheckCount == 'N' ||
            (isAcmic && policy.billingDetails[0].badCheckCount == 'Y')) &&
          policy.billingDetails[0].mortageeFlag == 'N' &&
          !policy.hasAffinityGroup &&
          !(
            policy.policyCancellationReason &&
            (policy.policyCancellationReason.toLowerCase() ==
              'pay - non-payment of insurance premium' ||
              policy.policyCancellationReason.toLowerCase() ==
                'fpd - failure to pay deposit') &&
            policy.policyFlags.isEft == 'UNENROLLED'
          )
        );
      }
    });
  }

  private isPifDiscountWindowActive(policyInfo: any, pifDiscountAvailableUntil: string): boolean {
    if (!policyInfo.effDate || !pifDiscountAvailableUntil) return false;

    const isRenewalPolicy = isExpiringLivoPolicy(policyInfo, ['AUTO']);
    const effDatePlus5 = this.parseLocalDate(policyInfo.effDate, 5);
    const discountUntilDate = this.parseLocalDate(pifDiscountAvailableUntil);
    const today = this.parseLocalDate(new Date().toISOString().split('T')[0]);
    // console.log('isPifDiscountWindowActive :: pifDiscountAvailableUntil:', pifDiscountAvailableUntil);

    const isDiscountUntilMatchingNewTerm = effDatePlus5.getTime() === discountUntilDate.getTime();

    return (isRenewalPolicy || isDiscountUntilMatchingNewTerm) && (today <= discountUntilDate);
  }

  private parseLocalDate(dateString: string, addDays = 0): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day + addDays);
    date.setHours(0, 0, 0, 0);
    return date;
  }
}
